import { useLocation } from "react-router-dom";
import { SafeOutlet } from "@/shared/components/ErrorBoundary";
import { PatientSidebar } from "./PatientSidebar";
import { PatientHeader } from "./PatientHeader";
import { usePatientProfile } from "@/portals/patient/context/PatientProfileContext";
import { useFamily } from "@/portals/patient/context/FamilyContext";
import { useAutoRefreshClinicalData } from "@/portals/patient/hooks/useAutoRefreshClinicalData";
import { usePatientLiveQueries } from "@/portals/patient/hooks/usePatientLiveQueries";
import { UploadToastStack } from "@/features/patient/records/components/UploadToastStack";

const pageMeta: Record<string, { titleBlack: string; titleRed: string; description: string }> = {
  "/patient/upload":           { titleBlack: "Upload Records",        titleRed: "", description: "Upload and manage your medical documents" },
  "/patient/documents":        { titleBlack: "Document Panel",        titleRed: "", description: "Every file you've sent us. Browse the original alongside what we found in it." },
  "/patient/human-review-queue": { titleBlack: "Human Review Queue",  titleRed: "", description: "Review and confirm information we couldn't confidently process from your records." },
  "/patient/human-review-queue/review-all": { titleBlack: "Bulk review",  titleRed: "", description: "Every medicine and record that needs your answer, in one place." },
  "/patient/patient-only-queue": { titleBlack: "Patient Only Queue",  titleRed: "", description: "Confirm that your uploads are yours. Only you can answer these." },
  "/patient/patient-only-queue/review-all": { titleBlack: "Bulk review",  titleRed: "", description: "Every upload you still need to confirm, in one place." },
  "/patient/health-concerns":  { titleBlack: "Health Concerns",       titleRed: "", description: "Track and manage your health concerns" },
  "/patient/timeline":         { titleBlack: "Health Timeline",       titleRed: "", description: "View your complete health history" },
  "/patient/ask":              { titleBlack: "Ask About Records",     titleRed: "", description: "Get insights from your health records" },
  "/patient/manage-family":    { titleBlack: "Manage Family",        titleRed: "", description: "Manage the family members whose health records you oversee." },
  "/patient/manage-caregivers": { titleBlack: "Manage Caregivers",   titleRed: "", description: "Control who can access your health records and what they can view." },
  "/patient/consent-privacy":  { titleBlack: "Consent & privacy",     titleRed: "", description: "This is your record of what you've agreed to and what you control. You can change most of these at any time, but a few are required to keep using MedSyncIQ." },
  "/patient/diagnosis":        { titleBlack: "Diagnoses",             titleRed: "", description: "Your medical diagnoses explained" },
  "/patient/complete-history": { titleBlack: "Complete Health History", titleRed: "", description: "Your full medical history in one place" },
  "/patient/medications":      { titleBlack: "My Medications",        titleRed: "", description: "Your current and past medications" },
  "/patient/diagnostics":      { titleBlack: "Diagnostics & Imaging", titleRed: "", description: "Your lab results and imaging studies" },
  "/patient/care-treatment":   { titleBlack: "Care & Treatment",      titleRed: "", description: "Your visits, your doctors’ notes, and calls with your care team" },
  "/patient/insurance-cards":  { titleBlack: "Insurance & Cards",     titleRed: "", description: "Your insurance coverage and cards" },
  "/patient/claims":           { titleBlack: "Insurance Claims",      titleRed: "", description: "Track your insurance claims" },
  "/patient/fhir-export":      { titleBlack: "FHIR Export",           titleRed: "", description: "Export your health data" },
  "/patient/profile":          { titleBlack: "Profile",               titleRed: "", description: "View and make changes to your profile." },
};

export function PatientLayout() {
  const location = useLocation();
  const { profile } = usePatientProfile();
  const { activeDependent } = useFamily();
  const meta =
    location.pathname === "/patient" && activeDependent
      ? {
          titleBlack: "Family Health Records",
          titleRed: "",
          description: "Access and manage the medical records and healthcare information of the family members under your care.",
        }
      // /patient/diagnostics/imaging/:studyId has no static pageMeta entry
      // (it carries a dynamic study id) — it replaces the Diagnostics &
      // Imaging content area, so it reuses that page's own header meta.
      : location.pathname.startsWith("/patient/diagnostics/imaging/")
        ? pageMeta["/patient/diagnostics"]
        : location.pathname.startsWith("/patient/medications/review/")
          ? { titleBlack: "Medication Review", titleRed: "", description: "Tell us about this medicine so a clinician can confirm it." }
          : pageMeta[location.pathname];
  useAutoRefreshClinicalData();
  usePatientLiveQueries();

  return (
    <div className="flex h-screen overflow-hidden bg-[#F7F7F7]">
      <PatientSidebar profile={profile} />
      <div className="flex flex-1 flex-col overflow-hidden">
        <PatientHeader
          profile={profile}
          titleBlack={meta?.titleBlack}
          titleRed={meta?.titleRed}
          description={meta?.description}
        />
        <main id="main-content" className="msq-route-main flex-1 overflow-y-auto bg-[#F9F9F9]">
          <SafeOutlet />
        </main>
      </div>
      <UploadToastStack />
    </div>
  );
}
